/**
 * LoadCalculationPanel - Panel for viewing and calculating HVAC loads
 * Shows both quick estimates and detailed Manual J calculations
 * Supports both floor plan mode and manual data entry mode
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  calculateRoomHeatingLoad,
  calculateRoomCoolingLoad,
  calculateTotalHeatingLoad,
  calculateTotalCoolingLoad,
  quickEstimateHeating,
  quickEstimateCooling,
  btuToTons,
  getRecommendedSize,
  formatLoad,
} from '../../utils/loadCalculations';
import {
  CLIMATE_ZONES,
  DEFAULT_DESIGN_CONDITIONS,
  DUCT_LOCATIONS,
  INFILTRATION_LEVELS,
  DAILY_RANGE,
} from '../../constants/climateZones';

// Wall construction options
const WALL_CONSTRUCTIONS = [
  { value: 'wood-2x4', label: '2x4 Wood Frame' },
  { value: 'wood-2x6', label: '2x6 Wood Frame' },
  { value: 'brick-veneer', label: 'Brick Veneer' },
  { value: 'concrete-block', label: 'Concrete Block' },
  { value: 'sip', label: 'SIP Panels' },
  { value: 'icf', label: 'ICF' },
];

const WALL_INSULATIONS = [
  { value: 'none', label: 'None' },
  { value: 'R-11', label: 'R-11' },
  { value: 'R-13', label: 'R-13' },
  { value: 'R-15', label: 'R-15' },
  { value: 'R-19', label: 'R-19' },
  { value: 'R-21', label: 'R-21' },
  { value: 'R-30', label: 'R-30' },
];

const WINDOW_TYPES = [
  { value: 'single-pane', label: 'Single Pane' },
  { value: 'double-clear', label: 'Double Pane (Clear)' },
  { value: 'double-low-e', label: 'Double Pane (Low-E)' },
  { value: 'triple-pane', label: 'Triple Pane' },
];

const CEILING_TYPES = [
  { value: 'below-vented-attic', label: 'Below Vented Attic (R-38)' },
  { value: 'below-unconditioned-attic', label: 'Below Unconditioned Attic (R-30)' },
  { value: 'cathedral', label: 'Cathedral Ceiling (R-25)' },
  { value: 'flat-roof', label: 'Flat Roof (R-22)' },
  { value: 'below-conditioned', label: 'Below Conditioned Space' },
];

const FLOOR_TYPES = [
  { value: 'slab', label: 'Slab on Grade' },
  { value: 'crawlspace-vented', label: 'Vented Crawlspace' },
  { value: 'crawlspace-sealed', label: 'Sealed Crawlspace' },
  { value: 'basement-unconditioned', label: 'Unconditioned Basement' },
  { value: 'basement-conditioned', label: 'Conditioned Basement' },
  { value: 'open-air', label: 'Open Air (Pier/Beam)' },
];

/**
 * Design conditions editor
 */
function DesignConditionsSection({ conditions, onChange, expanded, onToggle }) {
  const climateZone = CLIMATE_ZONES.find(z => z.value === conditions.climateZone);

  return (
    <div style={styles.section}>
      <button onClick={onToggle} style={styles.sectionHeader}>
        <span style={styles.sectionTitle}>Design Conditions</span>
        <span style={styles.expandIcon}>{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div style={styles.sectionContent}>
          {/* Climate Zone */}
          <div style={styles.field}>
            <label style={styles.label}>Climate Zone</label>
            <select
              value={conditions.climateZone}
              onChange={(e) => {
                const zone = CLIMATE_ZONES.find(z => z.value === e.target.value);
                onChange({
                  ...conditions,
                  climateZone: e.target.value,
                  heatingOutdoor: zone?.heating ?? conditions.heatingOutdoor,
                  coolingOutdoor: zone?.cooling ?? conditions.coolingOutdoor,
                });
              }}
              style={styles.select}
            >
              {CLIMATE_ZONES.map(zone => (
                <option key={zone.value} value={zone.value}>{zone.label}</option>
              ))}
            </select>
          </div>

          {/* Outdoor Temps */}
          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Heating Outdoor (°F)</label>
              <input
                type="number"
                value={conditions.heatingOutdoor}
                onChange={(e) => onChange({ ...conditions, heatingOutdoor: parseInt(e.target.value) || 0 })}
                style={styles.input}
              />
            </div>
            <div style={styles.halfField}>
              <label style={styles.label}>Cooling Outdoor (°F)</label>
              <input
                type="number"
                value={conditions.coolingOutdoor}
                onChange={(e) => onChange({ ...conditions, coolingOutdoor: parseInt(e.target.value) || 0 })}
                style={styles.input}
              />
            </div>
          </div>

          {/* Indoor Temps */}
          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Indoor Heating (°F)</label>
              <input
                type="number"
                value={conditions.indoorHeating}
                onChange={(e) => onChange({ ...conditions, indoorHeating: parseInt(e.target.value) || 70 })}
                style={styles.input}
              />
            </div>
            <div style={styles.halfField}>
              <label style={styles.label}>Indoor Cooling (°F)</label>
              <input
                type="number"
                value={conditions.indoorCooling}
                onChange={(e) => onChange({ ...conditions, indoorCooling: parseInt(e.target.value) || 75 })}
                style={styles.input}
              />
            </div>
          </div>

          {/* Infiltration */}
          <div style={styles.field}>
            <label style={styles.label}>Infiltration Level</label>
            <select
              value={conditions.infiltrationLevel || 'average'}
              onChange={(e) => {
                const level = INFILTRATION_LEVELS.find(l => l.value === e.target.value);
                onChange({
                  ...conditions,
                  infiltrationLevel: e.target.value,
                  infiltrationACH: level?.ach ?? 0.5,
                });
              }}
              style={styles.select}
            >
              {INFILTRATION_LEVELS.map(level => (
                <option key={level.value} value={level.value}>{level.label}</option>
              ))}
            </select>
          </div>

          {/* Duct Location */}
          <div style={styles.field}>
            <label style={styles.label}>Duct Location</label>
            <select
              value={conditions.ductLocation || 'conditioned'}
              onChange={(e) => {
                const loc = DUCT_LOCATIONS.find(l => l.value === e.target.value);
                onChange({
                  ...conditions,
                  ductLocation: e.target.value,
                  ductHeatingFactor: loc?.heatingFactor ?? 1.0,
                  ductCoolingFactor: loc?.coolingFactor ?? 1.0,
                });
              }}
              style={styles.select}
            >
              {DUCT_LOCATIONS.map(loc => (
                <option key={loc.value} value={loc.value}>{loc.label}</option>
              ))}
            </select>
          </div>

          {/* Daily Range */}
          <div style={styles.field}>
            <label style={styles.label}>Daily Temperature Range</label>
            <select
              value={conditions.dailyRange || 'medium'}
              onChange={(e) => {
                const range = DAILY_RANGE.find(r => r.value === e.target.value);
                onChange({
                  ...conditions,
                  dailyRange: e.target.value,
                  dailyRangeFactor: range?.factor ?? 0.85,
                });
              }}
              style={styles.select}
            >
              {DAILY_RANGE.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Quick estimate section
 */
function QuickEstimateSection({ totalArea, climateZone, expanded, onToggle }) {
  const [options, setOptions] = useState({
    ceilingHeight: 8,
    insulation: 'average',
    largeWindows: false,
    sunExposure: 'partial',
  });

  const heatingEstimate = useMemo(() =>
    quickEstimateHeating(totalArea, climateZone, options),
    [totalArea, climateZone, options]
  );

  const coolingEstimate = useMemo(() =>
    quickEstimateCooling(totalArea, climateZone, options),
    [totalArea, climateZone, options]
  );

  return (
    <div style={styles.section}>
      <button onClick={onToggle} style={styles.sectionHeader}>
        <span style={styles.sectionTitle}>Quick Estimate</span>
        <span style={styles.expandIcon}>{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div style={styles.sectionContent}>
          <p style={styles.hint}>
            Rule-of-thumb calculation based on square footage and climate zone.
          </p>

          {/* Show total area being used */}
          <div style={{ ...styles.resultRow, marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <span style={styles.resultLabel}>Total Floor Area:</span>
            <span style={{ ...styles.resultValue, color: '#00c8ff' }}>{totalArea} sq ft</span>
          </div>

          {/* Quick options */}
          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Ceiling Height</label>
              <input
                type="number"
                value={options.ceilingHeight}
                onChange={(e) => setOptions({ ...options, ceilingHeight: parseFloat(e.target.value) || 8 })}
                style={styles.input}
                step={0.5}
                min={7}
                max={20}
              />
            </div>
            <div style={styles.halfField}>
              <label style={styles.label}>Insulation</label>
              <select
                value={options.insulation}
                onChange={(e) => setOptions({ ...options, insulation: e.target.value })}
                style={styles.select}
              >
                <option value="poor">Poor</option>
                <option value="average">Average</option>
                <option value="good">Good</option>
                <option value="excellent">Excellent</option>
              </select>
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Sun Exposure</label>
              <select
                value={options.sunExposure}
                onChange={(e) => setOptions({ ...options, sunExposure: e.target.value })}
                style={styles.select}
              >
                <option value="shaded">Shaded</option>
                <option value="partial">Partial</option>
                <option value="full">Full Sun</option>
              </select>
            </div>
            <div style={styles.halfField}>
              <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="checkbox"
                  checked={options.largeWindows}
                  onChange={(e) => setOptions({ ...options, largeWindows: e.target.checked })}
                  style={{ accentColor: '#00c8ff' }}
                />
                Large Windows
              </label>
            </div>
          </div>

          {/* Results */}
          <div style={styles.resultsBox}>
            <div style={styles.resultRow}>
              <span style={styles.resultLabel}>Heating Load:</span>
              <span style={{ ...styles.resultValue, color: '#ff9800' }}>
                {formatLoad(heatingEstimate)} BTU/hr
              </span>
            </div>
            <div style={styles.resultRow}>
              <span style={styles.resultLabel}>Cooling Load:</span>
              <span style={{ ...styles.resultValue, color: '#2196f3' }}>
                {formatLoad(coolingEstimate)} BTU/hr ({btuToTons(coolingEstimate)} tons)
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Manual Entry Section - allows entering building data directly
 */
function ManualEntrySection({ manualData, onChange, expanded, onToggle }) {
  return (
    <div style={styles.section}>
      <button onClick={onToggle} style={styles.sectionHeader}>
        <span style={styles.sectionTitle}>Building Details</span>
        <span style={styles.expandIcon}>{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div style={styles.sectionContent}>
          {/* Floor Area and Ceiling Height */}
          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Total Floor Area (sq ft)</label>
              <input
                type="number"
                value={manualData.floorArea}
                onChange={(e) => onChange({ ...manualData, floorArea: parseInt(e.target.value) || 0 })}
                style={styles.input}
                min={0}
                step={100}
              />
            </div>
            <div style={styles.halfField}>
              <label style={styles.label}>Ceiling Height (ft)</label>
              <input
                type="number"
                value={manualData.ceilingHeight}
                onChange={(e) => onChange({ ...manualData, ceilingHeight: parseFloat(e.target.value) || 8 })}
                style={styles.input}
                min={7}
                max={20}
                step={0.5}
              />
            </div>
          </div>

          {/* Stories */}
          <div style={styles.field}>
            <label style={styles.label}>Number of Stories</label>
            <select
              value={manualData.stories}
              onChange={(e) => onChange({ ...manualData, stories: parseInt(e.target.value) })}
              style={styles.select}
            >
              <option value={1}>1 Story</option>
              <option value={2}>2 Stories</option>
              <option value={3}>3 Stories</option>
            </select>
          </div>

          <div style={{ ...styles.hint, marginTop: '12px', marginBottom: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
            Wall Construction
          </div>

          {/* Wall Construction */}
          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Wall Type</label>
              <select
                value={manualData.wallConstruction}
                onChange={(e) => onChange({ ...manualData, wallConstruction: e.target.value })}
                style={styles.select}
              >
                {WALL_CONSTRUCTIONS.map(w => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
            <div style={styles.halfField}>
              <label style={styles.label}>Wall Insulation</label>
              <select
                value={manualData.wallInsulation}
                onChange={(e) => onChange({ ...manualData, wallInsulation: e.target.value })}
                style={styles.select}
              >
                {WALL_INSULATIONS.map(i => (
                  <option key={i.value} value={i.value}>{i.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ ...styles.hint, marginTop: '12px', marginBottom: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
            Windows
          </div>

          {/* Windows */}
          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Window Type</label>
              <select
                value={manualData.windowType}
                onChange={(e) => onChange({ ...manualData, windowType: e.target.value })}
                style={styles.select}
              >
                {WINDOW_TYPES.map(w => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
            <div style={styles.halfField}>
              <label style={styles.label}>Window Area (sq ft)</label>
              <input
                type="number"
                value={manualData.windowArea}
                onChange={(e) => onChange({ ...manualData, windowArea: parseInt(e.target.value) || 0 })}
                style={styles.input}
                min={0}
              />
            </div>
          </div>

          {/* Window area hint */}
          <p style={{ ...styles.hint, marginTop: '0', fontSize: '10px' }}>
            Typical: 12-18% of floor area ({Math.round(manualData.floorArea * 0.12)}-{Math.round(manualData.floorArea * 0.18)} sq ft)
          </p>

          <div style={{ ...styles.hint, marginTop: '12px', marginBottom: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
            Ceiling & Floor
          </div>

          {/* Ceiling and Floor */}
          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Ceiling Type</label>
              <select
                value={manualData.ceilingType}
                onChange={(e) => onChange({ ...manualData, ceilingType: e.target.value })}
                style={styles.select}
              >
                {CEILING_TYPES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div style={styles.halfField}>
              <label style={styles.label}>Floor Type</label>
              <select
                value={manualData.floorType}
                onChange={(e) => onChange({ ...manualData, floorType: e.target.value })}
                style={styles.select}
              >
                {FLOOR_TYPES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ ...styles.hint, marginTop: '12px', marginBottom: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
            Doors & Occupants
          </div>

          {/* Doors and Occupants */}
          <div style={styles.row}>
            <div style={styles.halfField}>
              <label style={styles.label}>Exterior Doors</label>
              <input
                type="number"
                value={manualData.exteriorDoors}
                onChange={(e) => onChange({ ...manualData, exteriorDoors: parseInt(e.target.value) || 0 })}
                style={styles.input}
                min={1}
                max={10}
              />
            </div>
            <div style={styles.halfField}>
              <label style={styles.label}>Occupants</label>
              <input
                type="number"
                value={manualData.occupants}
                onChange={(e) => onChange({ ...manualData, occupants: parseInt(e.target.value) || 0 })}
                style={styles.input}
                min={0}
                max={20}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Room-by-room breakdown
 */
function RoomBreakdownSection({ roomsData, conditions, expanded, onToggle }) {
  const roomResults = useMemo(() => {
    return (roomsData || []).map(room => ({
      ...room,
      heating: calculateRoomHeatingLoad(room, conditions),
      cooling: calculateRoomCoolingLoad(room, conditions),
    }));
  }, [roomsData, conditions]);

  return (
    <div style={styles.section}>
      <button onClick={onToggle} style={styles.sectionHeader}>
        <span style={styles.sectionTitle}>Room-by-Room ({roomsData?.length || 0})</span>
        <span style={styles.expandIcon}>{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div style={styles.sectionContent}>
          {roomResults.length === 0 ? (
            <p style={styles.noData}>
              No room data available. Use the Room Data tool to add room information.
            </p>
          ) : (
            <div style={styles.roomList}>
              {roomResults.map((room, index) => (
                <RoomLoadCard key={room.roomId || index} room={room} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Individual room load card
 */
function RoomLoadCard({ room }) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Map internal names to display labels
  const roomTypeLabels = {
    'living-room': 'Living Room',
    'living': 'Living Room',
    'Living Room': 'Living Room',
    'family-room': 'Family Room',
    'family': 'Family Room',
    'Family Room': 'Family Room',
    'master-bedroom': 'Master Bedroom',
    'master': 'Master Bedroom',
    'Master Bedroom': 'Master Bedroom',
    'bedroom': 'Bedroom',
    'Bedroom': 'Bedroom',
    'kitchen': 'Kitchen',
    'Kitchen': 'Kitchen',
    'dining-room': 'Dining Room',
    'dining': 'Dining Room',
    'Dining Room': 'Dining Room',
    'bathroom': 'Bathroom',
    'Bathroom': 'Bathroom',
    'laundry': 'Laundry',
    'Laundry': 'Laundry',
    'office': 'Office',
    'Office': 'Office',
    'bonus-room': 'Bonus Room',
    'bonus': 'Bonus Room',
    'Bonus Room': 'Bonus Room',
    'garage': 'Garage',
    'Garage': 'Garage',
    'other': 'Other',
    'Other': 'Other',
  };

  // Get display name - check label mapping first, then use name directly if it looks like a proper name
  const getDisplayName = (name) => {
    if (!name) return 'Unknown Room';
    if (roomTypeLabels[name]) return roomTypeLabels[name];
    // If name has spaces or starts with uppercase, it's probably already a display name
    if (name.includes(' ') || name[0] === name[0].toUpperCase()) return name;
    // Otherwise capitalize it
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <div style={styles.roomCard}>
      <div style={styles.roomHeader}>
        <span style={styles.roomName}>
          {getDisplayName(room.name)}
        </span>
        <span style={styles.roomArea}>{room.floorArea || 0} sq ft</span>
      </div>

      <div style={styles.roomLoads}>
        <div style={styles.loadItem}>
          <span style={{ color: '#ff9800' }}>🔥</span>
          <span style={styles.loadValue}>{formatLoad(room.heating?.total || 0)}</span>
        </div>
        <div style={styles.loadItem}>
          <span style={{ color: '#2196f3' }}>❄️</span>
          <span style={styles.loadValue}>{formatLoad(room.cooling?.total || 0)}</span>
        </div>
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          style={styles.detailsBtn}
        >
          {showBreakdown ? '▲' : '▼'}
        </button>
      </div>

      {showBreakdown && (
        <div style={styles.breakdown}>
          <div style={styles.breakdownSection}>
            <div style={styles.breakdownTitle}>Heating Breakdown</div>
            {Object.entries(room.heating?.breakdown || {}).map(([key, value]) => (
              <div key={key} style={styles.breakdownRow}>
                <span style={styles.breakdownLabel}>{key}:</span>
                <span style={styles.breakdownValue}>{formatLoad(value)}</span>
              </div>
            ))}
          </div>
          <div style={styles.breakdownSection}>
            <div style={styles.breakdownTitle}>Cooling Breakdown</div>
            {Object.entries(room.cooling?.breakdown || {}).map(([key, value]) => (
              <div key={key} style={styles.breakdownRow}>
                <span style={styles.breakdownLabel}>{key}:</span>
                <span style={styles.breakdownValue}>{formatLoad(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Total summary section
 */
function TotalSummarySection({ roomsData, conditions }) {
  const totals = useMemo(() => {
    const heating = calculateTotalHeatingLoad(roomsData, conditions);
    const cooling = calculateTotalCoolingLoad(roomsData, conditions);
    return { heating, cooling };
  }, [roomsData, conditions]);

  const recommendedCooling = getRecommendedSize(totals.cooling.total, 'cooling');
  const recommendedHeating = getRecommendedSize(totals.heating.total, 'heating');

  return (
    <div style={styles.summaryBox}>
      <div style={styles.summaryTitle}>Total Building Load</div>

      <div style={styles.summaryRow}>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>Heating</span>
          <span style={{ ...styles.summaryValue, color: '#ff9800' }}>
            {formatLoad(totals.heating.total)} BTU/hr
          </span>
          <span style={styles.summaryRecommend}>
            Recommended: {formatLoad(recommendedHeating.btu)} BTU furnace
          </span>
        </div>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>Cooling</span>
          <span style={{ ...styles.summaryValue, color: '#2196f3' }}>
            {formatLoad(totals.cooling.total)} BTU/hr ({recommendedCooling.actualTons} tons)
          </span>
          <span style={styles.summaryRecommend}>
            Recommended: {recommendedCooling.tons} ton AC
          </span>
        </div>
      </div>

      <div style={styles.summaryDetails}>
        <div style={styles.detailRow}>
          <span>Sensible Cooling:</span>
          <span>{formatLoad(totals.cooling.sensible)} BTU/hr</span>
        </div>
        <div style={styles.detailRow}>
          <span>Latent Cooling:</span>
          <span>{formatLoad(totals.cooling.latent)} BTU/hr</span>
        </div>
        <div style={styles.detailRow}>
          <span>Duct Losses (heating):</span>
          <span>{formatLoad(totals.heating.ductLoss)} BTU/hr</span>
        </div>
        <div style={styles.detailRow}>
          <span>Duct Losses (cooling):</span>
          <span>{formatLoad(totals.cooling.ductLoss)} BTU/hr</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Main LoadCalculationPanel component
 */
export default function LoadCalculationPanel({
  rooms = [],
  roomsData = [],
  totalArea = 0,
  onClose,
  isMobile = false,
}) {
  // Mode: 'floorplan' or 'manual'
  const [mode, setMode] = useState('floorplan');

  const [conditions, setConditions] = useState({
    ...DEFAULT_DESIGN_CONDITIONS,
    infiltrationLevel: 'average',
    infiltrationACH: 0.5,
    ductLocation: 'conditioned',
    ductHeatingFactor: 1.0,
    ductCoolingFactor: 1.0,
    dailyRangeFactor: 0.85,
  });

  // Manual entry data
  const [manualData, setManualData] = useState({
    floorArea: totalArea || 1600,
    ceilingHeight: 8,
    stories: 1,
    wallConstruction: 'wood-2x4',
    wallInsulation: 'R-13',
    windowType: 'double-low-e',
    windowArea: Math.round((totalArea || 1600) * 0.15),
    ceilingType: 'below-vented-attic',
    floorType: 'slab',
    exteriorDoors: 2,
    occupants: 3,
  });

  const [expandedSection, setExpandedSection] = useState('summary');

  // Convert manual entry data to room format for calculations
  const manualRoomsData = useMemo(() => {
    if (mode !== 'manual' || !manualData.floorArea) return [];

    const { floorArea, ceilingHeight, stories, wallConstruction, wallInsulation,
            windowType, windowArea, ceilingType, floorType, exteriorDoors, occupants } = manualData;

    // Calculate building dimensions (assume roughly square footprint)
    const footprintArea = floorArea / stories;
    const buildingSide = Math.sqrt(footprintArea);

    // Distribute window area across 4 orientations
    const windowPerWall = windowArea / 4;
    const windowWidth = Math.round(Math.sqrt(windowPerWall) * 12); // inches
    const windowHeight = Math.round(windowPerWall / (windowWidth / 12) * 12);

    // Create exterior walls (one per direction)
    const exteriorWalls = [
      { length: buildingSide, construction: wallConstruction, insulation: wallInsulation, facing: 'N' },
      { length: buildingSide, construction: wallConstruction, insulation: wallInsulation, facing: 'E' },
      { length: buildingSide, construction: wallConstruction, insulation: wallInsulation, facing: 'S' },
      { length: buildingSide, construction: wallConstruction, insulation: wallInsulation, facing: 'W' },
    ];

    // Create windows (one per direction, larger on S and W)
    const windows = [
      { width: windowWidth, height: windowHeight, type: windowType, frame: 'wood-vinyl', facing: 'N', shading: 'none' },
      { width: windowWidth, height: windowHeight, type: windowType, frame: 'wood-vinyl', facing: 'E', shading: 'none' },
      { width: Math.round(windowWidth * 1.2), height: windowHeight, type: windowType, frame: 'wood-vinyl', facing: 'S', shading: 'none' },
      { width: Math.round(windowWidth * 1.2), height: windowHeight, type: windowType, frame: 'wood-vinyl', facing: 'W', shading: 'none' },
    ];

    // Create doors
    const doors = [];
    for (let i = 0; i < exteriorDoors; i++) {
      doors.push({ width: 36, height: 80, type: i === 0 ? 'solid-window' : 'solid-wood' });
    }

    return [{
      roomId: 'manual-entry',
      name: 'Whole Building',
      conditioned: 'yes',
      floorArea: floorArea,
      ceilingHeight: ceilingHeight,
      ceilingType: ceilingType,
      floorType: floorType,
      floorInsulation: 'none',
      exteriorWalls: exteriorWalls,
      windows: windows,
      doors: doors,
      occupants: occupants,
    }];
  }, [mode, manualData]);

  // Merge rooms with roomsData - create roomData for rooms that don't have explicit data
  const mergedRoomsData = useMemo(() => {
    // Create a map of existing room data by roomId
    const roomDataMap = {};
    (roomsData || []).forEach(rd => {
      if (rd.roomId) {
        roomDataMap[rd.roomId] = rd;
      }
    });

    // Build merged list from all rooms
    const result = [];
    (rooms || []).forEach(room => {
      // Only skip rooms explicitly marked as unconditioned
      if (room.conditioned === 'no') return;

      // Check if we have explicit room data
      let roomData = roomDataMap[room.id];

      if (roomData) {
        // Use roomData but override name with the actual room name from canvas
        result.push({
          ...roomData,
          name: room.name || roomData.name,
        });
      } else {
        // Create fallback room data from room properties
        const points = room.points || room.vertices;
        let area = 0;
        if (points && points.length >= 3) {
          // Calculate area using shoelace formula
          for (let i = 0; i < points.length; i++) {
            const j = (i + 1) % points.length;
            area += points[i].x * points[j].y - points[j].x * points[i].y;
          }
          area = Math.abs(area) / 2 / (40 * 40); // Convert to sq ft (40 pixels per foot)
        }

        // Calculate reasonable defaults for exterior walls based on floor area
        // Assume room is roughly square with 4 exterior walls (conservative estimate)
        const floorArea = Math.round(area) || room.area || 0;
        const roomSide = Math.sqrt(floorArea); // Approximate side length
        const ceilingHeight = 8;

        // Default: 4 exterior walls with average insulation (all sides exposed)
        const defaultExteriorWalls = floorArea > 0 ? [
          { length: roomSide, construction: 'wood-2x4', insulation: 'R-13', facing: 'N' },
          { length: roomSide, construction: 'wood-2x4', insulation: 'R-13', facing: 'E' },
          { length: roomSide, construction: 'wood-2x4', insulation: 'R-13', facing: 'S' },
          { length: roomSide, construction: 'wood-2x4', insulation: 'R-13', facing: 'W' },
        ] : [];

        // Default windows: estimate ~15% of floor area as window space
        // Distributed across 4 walls with larger windows on S and W for solar gain
        const windowAreaTarget = floorArea * 0.15; // 15% of floor area
        const avgWindowArea = windowAreaTarget / 4; // per wall
        const windowWidth = Math.round(Math.sqrt(avgWindowArea) * 12); // convert to inches
        const windowHeight = Math.round(avgWindowArea / (windowWidth / 12) * 12);
        const defaultWindows = floorArea > 0 ? [
          { width: windowWidth, height: windowHeight, type: 'double-low-e', frame: 'wood-vinyl', facing: 'N', shading: 'none' },
          { width: windowWidth, height: windowHeight, type: 'double-low-e', frame: 'wood-vinyl', facing: 'E', shading: 'none' },
          { width: Math.round(windowWidth * 1.2), height: windowHeight, type: 'double-low-e', frame: 'wood-vinyl', facing: 'S', shading: 'none' },
          { width: Math.round(windowWidth * 1.2), height: windowHeight, type: 'double-low-e', frame: 'wood-vinyl', facing: 'W', shading: 'none' },
        ] : [];

        // Default: 1 exterior door for some room types
        const roomName = (room.name || 'bedroom').toLowerCase();
        const hasExteriorDoor = ['living', 'living-room', 'family', 'family-room', 'garage'].some(t => roomName.includes(t));
        const defaultDoors = hasExteriorDoor ? [
          { width: 36, height: 80, type: 'solid-wood' }
        ] : [];

        result.push({
          roomId: room.id,
          name: room.name || 'bedroom',
          conditioned: 'yes',
          floorArea: floorArea,
          ceilingHeight: ceilingHeight,
          ceilingType: 'below-vented-attic',
          windows: defaultWindows,
          exteriorWalls: defaultExteriorWalls,
          floorType: 'slab',
          floorInsulation: 'none',
          doors: defaultDoors,
        });
      }
    });

    return result;
  }, [rooms, roomsData]);

  // Select which data to use based on mode
  const activeRoomsData = mode === 'manual' ? manualRoomsData : mergedRoomsData;

  // Calculate total area from active rooms data for quick estimate
  const calculatedTotalArea = useMemo(() => {
    return activeRoomsData.reduce((sum, room) => sum + (room.floorArea || 0), 0);
  }, [activeRoomsData]);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isMobile ? 0 : '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#1a2332',
          borderRadius: isMobile ? '12px 12px 0 0' : '12px',
          width: isMobile ? '100%' : '480px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.headerTitle}>Load Calculations</span>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        {/* Mode Toggle */}
        <div style={styles.modeToggle}>
          <button
            onClick={() => setMode('floorplan')}
            style={{
              ...styles.modeBtn,
              ...(mode === 'floorplan' ? styles.modeBtnActive : {}),
            }}
          >
            From Floor Plan
          </button>
          <button
            onClick={() => setMode('manual')}
            style={{
              ...styles.modeBtn,
              ...(mode === 'manual' ? styles.modeBtnActive : {}),
            }}
          >
            Manual Entry
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Total Summary (always visible) */}
          <TotalSummarySection roomsData={activeRoomsData} conditions={conditions} />

          {/* Quick Estimate */}
          <QuickEstimateSection
            totalArea={calculatedTotalArea || totalArea}
            climateZone={conditions.climateZone}
            expanded={expandedSection === 'quick'}
            onToggle={() => toggleSection('quick')}
          />

          {/* Manual Entry Section - only show in manual mode */}
          {mode === 'manual' && (
            <ManualEntrySection
              manualData={manualData}
              onChange={setManualData}
              expanded={expandedSection === 'manual'}
              onToggle={() => toggleSection('manual')}
            />
          )}

          {/* Design Conditions */}
          <DesignConditionsSection
            conditions={conditions}
            onChange={setConditions}
            expanded={expandedSection === 'conditions'}
            onToggle={() => toggleSection('conditions')}
          />

          {/* Room Breakdown - only show in floorplan mode */}
          {mode === 'floorplan' && (
            <RoomBreakdownSection
              roomsData={mergedRoomsData}
              conditions={conditions}
              expanded={expandedSection === 'rooms'}
              onToggle={() => toggleSection('rooms')}
            />
          )}

          {/* Info footer */}
          <div style={styles.footer}>
            <p style={styles.disclaimer}>
              These calculations provide estimates based on Manual J methodology.
              For accurate sizing, consult a licensed HVAC contractor.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,200,255,0.05)',
  },
  headerTitle: {
    color: '#00c8ff',
    fontWeight: '600',
    fontSize: '14px',
  },
  closeBtn: {
    background: 'rgba(255,100,100,0.15)',
    border: 'none',
    color: '#ff6666',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  modeToggle: {
    display: 'flex',
    padding: '12px 20px',
    gap: '8px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.2)',
  },
  modeBtn: {
    flex: 1,
    padding: '10px 16px',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    background: 'transparent',
    color: '#8090a0',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  modeBtnActive: {
    background: 'rgba(0,200,255,0.15)',
    borderColor: 'rgba(0,200,255,0.4)',
    color: '#00c8ff',
    fontWeight: '600',
  },
  content: {
    padding: '16px 20px',
  },
  section: {
    marginBottom: '12px',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.05)',
    border: 'none',
    cursor: 'pointer',
    color: '#d0d8e0',
  },
  sectionTitle: {
    fontWeight: '600',
    fontSize: '12px',
  },
  expandIcon: {
    fontSize: '10px',
    color: '#6080a0',
  },
  sectionContent: {
    padding: '12px 14px',
  },
  field: {
    marginBottom: '10px',
  },
  row: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  halfField: {
    flex: 1,
  },
  label: {
    display: 'block',
    color: '#8090a0',
    fontSize: '11px',
    marginBottom: '4px',
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    background: '#0d1117',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '12px',
  },
  select: {
    width: '100%',
    padding: '8px 10px',
    background: '#0d1117',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '12px',
  },
  hint: {
    color: '#6080a0',
    fontSize: '11px',
    marginBottom: '12px',
    fontStyle: 'italic',
  },
  resultsBox: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '6px',
    padding: '12px',
    marginTop: '12px',
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  resultLabel: {
    color: '#8090a0',
    fontSize: '12px',
  },
  resultValue: {
    fontWeight: '600',
    fontSize: '13px',
  },
  noData: {
    color: '#6080a0',
    fontSize: '12px',
    textAlign: 'center',
    padding: '20px',
  },
  roomList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  roomCard: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '6px',
    padding: '10px 12px',
  },
  roomHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  roomName: {
    color: '#d0d8e0',
    fontSize: '12px',
    fontWeight: '600',
  },
  roomArea: {
    color: '#6080a0',
    fontSize: '11px',
  },
  roomLoads: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  loadItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  loadValue: {
    color: '#d0d8e0',
    fontSize: '12px',
    fontWeight: '500',
  },
  detailsBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: '#6080a0',
    fontSize: '10px',
    cursor: 'pointer',
    padding: '2px 6px',
  },
  breakdown: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  breakdownSection: {
    fontSize: '10px',
  },
  breakdownTitle: {
    color: '#8090a0',
    fontWeight: '600',
    marginBottom: '6px',
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#6080a0',
    marginBottom: '2px',
  },
  breakdownLabel: {
    textTransform: 'capitalize',
  },
  breakdownValue: {
    color: '#a0a8b0',
  },
  summaryBox: {
    background: 'linear-gradient(135deg, rgba(0,200,255,0.1) 0%, rgba(76,175,80,0.1) 100%)',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid rgba(0,200,255,0.2)',
  },
  summaryTitle: {
    color: '#00c8ff',
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '12px',
    textAlign: 'center',
  },
  summaryRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
  },
  summaryItem: {
    flex: 1,
    textAlign: 'center',
  },
  summaryLabel: {
    display: 'block',
    color: '#8090a0',
    fontSize: '11px',
    marginBottom: '4px',
  },
  summaryValue: {
    display: 'block',
    fontWeight: '700',
    fontSize: '16px',
  },
  summaryRecommend: {
    display: 'block',
    color: '#4caf50',
    fontSize: '10px',
    marginTop: '4px',
  },
  summaryDetails: {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    paddingTop: '10px',
    fontSize: '11px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#6080a0',
    marginBottom: '4px',
  },
  footer: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  disclaimer: {
    color: '#6080a0',
    fontSize: '10px',
    textAlign: 'center',
    fontStyle: 'italic',
    margin: 0,
  },
};
